@if (loading()) {
  <div class="flex items-center justify-center h-full">
    <div
      class="w-8 h-8 rounded-full border-2 border-accent-500 border-t-transparent animate-spin"
    ></div>
  </div>
} @else if (investigation(); as inv) {
  <div class="flex h-full min-h-0 overflow-hidden border-t border-surface-300 mt-1 relative">
    <!-- ── LEFT SIDEBAR -->
    <aside
      class="hidden lg:flex z-10 flex-col w-56 xl:w-64 shrink-0 bg-surface-50 border-r border-surface-300 overflow-y-auto p-5 gap-5"
    >
      @if (addingIncident()) {
        <!-- ── Add incident form ── -->
        <div class="flex flex-col gap-4">
          <div>
            <p class="text-xs font-bold text-navy-900 mb-0.5">Link New Incident</p>
            <p class="text-2xs text-slate-500">Select an incident to link to this investigation.</p>
          </div>

          <div class="flex flex-col gap-1.5">
            <label class="text-2xs font-semibold text-slate-500 uppercase tracking-wider"
              >Incident</label
            >
            <app-select
              placeholder="Select incident…"
              [options]="incidentOptions()"
              [value]="newIncidentId()"
              [loading]="loadingIncidentOptions()"
              [hasMore]="incidentsHasMore()"
              [searchable]="true"
              (changed)="newIncidentId.set($event)"
              (loadMore)="onLoadMoreIncidents()"
            />
          </div>

          <div class="flex flex-col gap-2 mt-1">
            <button
              type="button"
              (click)="onSaveNewIncident()"
              [disabled]="!newIncidentId() || savingIncident()"
              class="w-full py-2.5 text-xs font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {{ savingIncident() ? 'Saving…' : 'Save' }}
            </button>
            <button
              type="button"
              (click)="cancelAddIncident()"
              [disabled]="savingIncident()"
              class="w-full py-2 text-xs font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      } @else {
        <!-- ── Case Context ── -->
        <div class="flex flex-col">
          <p class="text-2xs leading-3 font-normal text-slate-500">Case Context</p>

          @if (activeIncident(); as inc) {
            <!-- Linked Incident row -->
            <div class="flex items-center justify-between mb-6">
              <p class="text-sm leading-4 font-semibold text-navy-600">Linked Incident</p>
              <button
                type="button"
                (click)="goToIncident(inc.incidentId, $event)"
                class="text-sm font-bold text-accent-500 hover:text-accent-600 transition-colors cursor-pointer"
              >
                #{{ inc.incidentNumber }}
              </button>
            </div>

            <!-- Stage badge -->
            @if (activeIncident()) {
              <div
                class="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-surface-200 shadow-sm"
              >
                <div
                  class="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center shrink-0"
                >
                  <img src="/icons/check-white-icon.svg" class="w-4 h-4" alt="" />
                </div>
                <div>
                  @if (!activeStageIsTerminal()) {
                    <p class="text-2xs text-slate-400">Stage {{ activeStage() }}</p>
                  }
                  <p class="text-xs font-bold text-navy-900">{{ activeStageMeta().name }}</p>
                </div>
              </div>
            } @else {
              <div
                class="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-surface-200"
              >
                <div class="w-9 h-9 rounded-full bg-surface-200 animate-pulse shrink-0"></div>
                <div class="flex flex-col gap-2 flex-1">
                  <div class="h-2 rounded bg-surface-200 animate-pulse w-1/3"></div>
                  <div class="h-3 rounded bg-surface-200 animate-pulse w-2/3"></div>
                </div>
              </div>
            }
          }
        </div>

        <div class="h-px bg-surface-200"></div>

        <!-- Investigating Program -->
        @if (inv.investigatingProgramName) {
          <div class="flex flex-col gap-1">
            <p class="text-xs leading-4 text-slate-500">Investigating Program</p>
            <p class="text-xs leading-4 font-bold text-navy-900">
              {{ inv.investigatingProgramName }}
            </p>
          </div>
        }

        <!-- Person Sending Notice -->
        @if (inv.personSendingNoticeName) {
          <div class="flex flex-col gap-2">
            <p class="text-xs leading-4 text-slate-500">Person Sending Notice</p>
            <div class="flex items-center gap-2.5">
              <div
                class="w-6 h-6 rounded-full bg-surface-100 flex items-center justify-center shrink-0"
              >
                <img src="/icons/user-icon.svg" class="w-3.5 h-3.5 opacity-40" alt="" />
              </div>
              <span class="text-xs font-bold text-navy-900">{{ inv.personSendingNoticeName }}</span>
            </div>
          </div>
        }

        <!-- Clients Involved -->
        @if (activeIncident()?.piInvolvements?.length) {
          <div class="flex flex-col gap-2">
            <p class="text-xs text-slate-500">Clients Involved</p>
            <div class="flex flex-col gap-2">
              @for (pi of activeIncident()!.piInvolvements; track pi.id) {
                <!-- Involvement type below the name, so both fit the narrow panel. -->
                <div class="flex items-start gap-2.5">
                  <div
                    class="w-6 h-6 rounded-full bg-surface-100 flex items-center justify-center shrink-0"
                  >
                    <img src="/icons/user-icon.svg" class="w-3.5 h-3.5 opacity-40" alt="" />
                  </div>
                  <div class="flex flex-col min-w-0 pt-0.5">
                    <span class="text-xs font-bold text-navy-900 wrap-break-word">
                      {{ pi.firstName }} {{ pi.surnames }}
                    </span>
                    @if (pi.piInvolvementType.name) {
                      <span class="text-2xs text-slate-500 wrap-break-word">{{
                        pi.piInvolvementType.name
                      }}</span>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Incident Category: the linked incident's categories -->
        @if (activeIncident()?.incidentCategorizations?.length) {
          <div class="flex flex-col gap-2">
            <p class="text-xs text-slate-500">Incident Category</p>
            <div class="flex flex-wrap gap-1.5">
              @for (cat of activeIncident()!.incidentCategorizations ?? []; track cat.id) {
                <span
                  class="px-3 py-1 text-xs font-semibold text-navy-600 border border-surface-300 rounded-full bg-white"
                >
                  {{ cat.situationCategoryName }}
                </span>
              }
            </div>
          </div>
        }

        <div class="h-px bg-surface-200"></div>

        <!-- Timeline -->
        <div class="flex flex-col gap-1">
          <p class="text-xs font-semibold text-slate-500 mb-2">Timeline</p>

          <!-- Items with vertical connector -->
          <div class="flex flex-col">
            @if (activeIncident()?.incidentDate) {
              <div class="flex gap-3">
                <div class="flex flex-col items-center">
                  <div
                    class="w-6 h-6 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0"
                  >
                    <img src="/icons/clock-icon.svg" class="w-3 h-3 opacity-50" alt="" />
                  </div>
                  @if (inv.investigationNoticeDate || inv.findingDate) {
                    <div class="w-px flex-1 bg-surface-300 my-1.5"></div>
                  }
                </div>
                <div class="pb-4">
                  <p class="text-xs text-slate-400">Incident Occurred</p>
                  <p class="text-xs font-bold text-navy-900">
                    {{ formatDate(activeIncident()!.incidentDate) }}
                  </p>
                </div>
              </div>
            }
            @if (inv.investigationNoticeDate) {
              <div class="flex gap-3">
                <div class="flex flex-col items-center">
                  <div
                    class="w-6 h-6 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0"
                  >
                    <img src="/icons/clock-icon.svg" class="w-3 h-3 opacity-50" alt="" />
                  </div>
                  @if (inv.findingDate) {
                    <div class="w-px flex-1 bg-surface-300 my-1.5"></div>
                  }
                </div>
                <div class="pb-4">
                  <p class="text-xs text-slate-400">Notice Generated</p>
                  <p class="text-xs font-bold text-navy-900">
                    {{ formatDate(inv.investigationNoticeDate) }}
                  </p>
                </div>
              </div>
            }
            @if (inv.findingDate) {
              <div class="flex gap-3">
                <div class="flex flex-col items-center">
                  <div
                    class="w-6 h-6 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0"
                  >
                    <img src="/icons/clock-icon.svg" class="w-3 h-3 opacity-50" alt="" />
                  </div>
                </div>
                <div>
                  <p class="text-xs text-slate-400">Finding Date</p>
                  <p class="text-xs font-bold text-navy-900">{{ formatDate(inv.findingDate) }}</p>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Remove incident -->
        @if (canEdit() && activeIncident()) {
          <div class="h-px bg-surface-200"></div>
          <div class="flex flex-col gap-1.5">
            <button
              type="button"
              (click)="askRemoveIncident(activeIncident()!)"
              [disabled]="!canRemoveIncident() || removingIncident()"
              class="flex items-center justify-center gap-2 w-full py-2.5 text-xs font-semibold text-red-500 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              <img src="/icons/trash-red-icon.svg" class="w-3.5 h-3.5" alt="" />
              {{ removingIncident() ? 'Removing…' : 'Remove incident' }}
            </button>
            @if (!canRemoveIncident()) {
              <p class="text-2xs text-slate-400 text-center">
                An investigation needs at least one linked incident.
              </p>
            }
          </div>
        }
      }
    </aside>

    <!-- ── VERTICAL INCIDENT TABS -->
    <div class="hidden lg:flex flex-col items-start shrink-0 z-20 -ml-px bg-white">
      @for (inc of inv.incidentInvestigations; track inc.id; let i = $index; let last = $last) {
        <button
          type="button"
          (click)="activeIncidentIndex.set(i); addingIncident.set(false)"
          class="flex items-center justify-center w-8 py-4 text-2xs font-bold transition-all cursor-pointer shrink-0 bg-white border-r border-surface-300"
          [ngClass]="[
            !addingIncident() && activeIncidentIndex() === i
              ? 'text-navy-800 bg-surface-50!'
              : 'text-slate-500 hover:text-navy-600',
            i === 0 ? 'rounded-tr-xl border-t' : '',
            last && !canEdit() ? 'rounded-br-xl border-b' : '',
          ]"
          [title]="'#' + inc.incidentNumber"
        >
          <span class="[writing-mode:vertical-rl] whitespace-nowrap leading-none">
            #{{ inc.incidentNumber }}
          </span>
        </button>
      }
      @if (canEdit()) {
        <button
          type="button"
          (click)="startAddIncident()"
          class="flex items-center justify-center w-8 py-3 transition-all cursor-pointer shrink-0 bg-white border border-l-0 border-t-0 border-surface-300 rounded-br-xl"
          [ngClass]="
            addingIncident()
              ? 'text-accent-500 border-accent-200'
              : 'text-slate-400 hover:text-accent-500'
          "
          title="Link new incident"
        >
          <span class="text-base font-bold leading-none">+</span>
        </button>
      }
    </div>

    <!-- ── MAIN CONTENT + EVIDENCE PANEL ────────────────────────────────────── -->
    <!-- min-w-0: without it this flex item can't shrink below its content and gets clipped on mobile -->
    <div class="flex-1 flex flex-col min-h-0 min-w-0">
      <div class="flex-1 overflow-y-auto px-4 sm:px-6 flex flex-col xl:flex-row bg-white">
        <!-- xl:self-start: in the flex-row scroll container a stretched column would be exactly the
           viewport height, so its bottom padding (and the Save button area) ended up clipped. -->
        <div
          class="flex-1 min-w-0 flex flex-col py-4 sm:py-6 xl:self-start xl:min-h-full xl:border-r border-surface-300"
        >
          <!-- Case Information (fields entered when the investigation was created) -->
          <div
            id="case-information"
            class="bg-white rounded-2xl py-5 sm:p-6 flex flex-col gap-5 scroll-mt-4"
          >
            <div>
              <p class="text-sm font-bold text-navy-600 leading-4">Case Information</p>
              <p class="text-xs text-slate-500 leading-4 mt-1.5">
                Details recorded when the investigation was opened
              </p>
            </div>

            @if (canEdit()) {
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-xs font-semibold text-navy-700">
                    Date of Notice <span class="text-red-500">*</span>
                  </label>
                  <app-date-picker
                    [value]="localNoticeDate()"
                    placeholder="Select date…"
                    [error]="showCaseError('noticeDate')"
                    (valueChange)="localNoticeDate.set($event)"
                  />
                  @if (showCaseError('noticeDate')) {
                    <p class="text-xs text-red-500">Required</p>
                  }
                </div>

                <div class="flex flex-col gap-1.5">
                  <label class="text-xs font-semibold text-navy-700">
                    Person Sending Notice <span class="text-red-500">*</span>
                  </label>
                  <app-person-select
                    [value]="localPersonId()"
                    [valueLabel]="inv.personSendingNoticeName"
                    [error]="showCaseError('person')"
                    (changed)="localPersonId.set($event)"
                  />
                  @if (showCaseError('person')) {
                    <p class="text-xs text-red-500">Required</p>
                  }
                </div>

                <div class="flex flex-col gap-1.5 sm:col-span-2">
                  <label class="text-xs font-semibold text-navy-700">
                    Investigating Program <span class="text-red-500">*</span>
                  </label>
                  <app-select
                    [options]="programSelectOptions()"
                    [value]="localProgramId()"
                    placeholder="Select program…"
                    [loading]="caseOptionsLoading()"
                    [searchable]="true"
                    [error]="showCaseError('program')"
                    (changed)="onProgramChange($event)"
                  />
                  @if (showCaseError('program')) {
                    <p class="text-xs text-red-500">Required</p>
                  }
                  @if (localProgramId() !== (inv.investigatingProgramId ?? '')) {
                    <p class="text-2xs text-amber-600">
                      Changing the program replaces the Additional Data fields below with the new
                      program's fields.
                    </p>
                  }
                </div>

                <div class="flex flex-col gap-1.5 sm:col-span-2">
                  <label class="text-xs font-semibold text-navy-700">
                    Allegation Categories <span class="text-red-500">*</span>
                  </label>
                  <app-multiselect-dropdown
                    [options]="categoryOptions()"
                    [selected]="localCategoryIds()"
                    placeholder="Select categories…"
                    [error]="showCaseError('categories')"
                    (selectedChange)="localCategoryIds.set($event)"
                  />
                  @if (showCaseError('categories')) {
                    <p class="text-xs text-red-500">Select at least one category</p>
                  }
                </div>
              </div>
            } @else {
              <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-1">
                  <dt class="text-xs text-slate-500">Date of Notice</dt>
                  <dd class="text-sm font-semibold text-navy-900">
                    {{ formatDate(inv.investigationNoticeDate) }}
                  </dd>
                </div>
                <div class="flex flex-col gap-1">
                  <dt class="text-xs text-slate-500">Person Sending Notice</dt>
                  <dd class="text-sm font-semibold text-navy-900">
                    {{ inv.personSendingNoticeName ?? '—' }}
                  </dd>
                </div>
                <div class="flex flex-col gap-1 sm:col-span-2">
                  <dt class="text-xs text-slate-500">Investigating Program</dt>
                  <dd class="text-sm font-semibold text-navy-900">
                    {{ inv.investigatingProgramName ?? '—' }}
                  </dd>
                </div>
                <div class="flex flex-col gap-1.5 sm:col-span-2">
                  <dt class="text-xs text-slate-500">Allegation Categories</dt>
                  <dd class="flex flex-wrap gap-1.5">
                    @for (cat of inv.investigationCategorizations; track cat.id) {
                      <span
                        class="px-3 py-1 text-xs font-semibold text-navy-600 border border-surface-300 rounded-full bg-white"
                      >
                        {{ cat.situationCategoryName }}
                      </span>
                    } @empty {
                      <span class="text-sm font-semibold text-navy-900">—</span>
                    }
                  </dd>
                </div>
              </dl>
            }
          </div>

          <div class="bg-white rounded-2xl py-5 sm:p-6 flex flex-col gap-5">
            <div>
              <p class="text-sm font-bold text-navy-600 leading-4">Case Process &amp; Findings</p>
              <p class="text-xs text-slate-500 leading-4 mt-1.5">
                Narrative documentation of procedures and follow-up actions
              </p>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-xs font-semibold text-navy-700">Investigation narrative</label>
              <textarea
                rows="6"
                [readonly]="!canEdit()"
                [value]="localNarrative()"
                (input)="localNarrative.set($any($event.target).value)"
                placeholder="Describe questions asked, information used, and procedures followed..."
                class="w-full px-4 py-3 text-sm border border-surface-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-accent-200 focus:border-accent-400 placeholder:text-slate-300 text-navy-900"
              ></textarea>
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-xs font-semibold text-navy-700">Investigation Follow-Up</label>
              <textarea
                rows="6"
                [readonly]="!canEdit()"
                [value]="localFollowup()"
                (input)="localFollowup.set($any($event.target).value)"
                placeholder="Describe the incident..."
                class="w-full px-4 py-3 text-sm border border-surface-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-accent-200 focus:border-accent-400 placeholder:text-slate-300 text-navy-900"
              ></textarea>
            </div>
          </div>

          <!-- Additional Data -->
          @if (cdeItems().length) {
            <div class="bg-white rounded-2xl py-5 sm:p-6 flex flex-col gap-5">
              <div>
                <p class="text-sm font-bold text-navy-600">Additional Data</p>
                <p class="text-xs text-slate-500 mt-1.5">
                  Custom investigation fields and responses.
                </p>
              </div>

              <fieldset
                [disabled]="!canEdit()"
                class="flex flex-col gap-4 min-w-0 border-0 p-0 m-0"
              >
                @for (cde of sortedCdePrograms(); track cde.id) {
                  @if (cde.configuredDataElement.cdeDataType?.dataType !== 'dummy') {
                    <div class="flex flex-col gap-1.5">
                      <label class="text-xs font-semibold text-navy-700">
                        {{ cde.configuredDataElement.name }}
                      </label>
                      @switch (cde.configuredDataElement.cdeDataType?.dataType) {
                        @case ('List Item') {
                          <app-select
                            placeholder="Select…"
                            [options]="listItemsToOptions(cde.configuredDataElement.cdeListItems)"
                            [keepOrder]="true"
                            [value]="
                              cdeValues().get(cde.configuredDataElementId)?.cdvListItemId ?? ''
                            "
                            (changed)="
                              onCdeChange(cde.configuredDataElementId, 'List Item', $event)
                            "
                          />
                        }
                        @case ('Note') {
                          <textarea
                            rows="3"
                            placeholder="Answer"
                            class="w-full resize-none"
                            [value]="cdeValues().get(cde.configuredDataElementId)?.cdvNote ?? ''"
                            (change)="
                              onCdeChange(
                                cde.configuredDataElementId,
                                'Note',
                                $any($event.target).value
                              )
                            "
                          ></textarea>
                        }
                        @case ('Datetime') {
                          <app-date-picker
                            placeholder="Select date…"
                            [value]="
                              cdeValues().get(cde.configuredDataElementId)?.cdvDatetime ?? ''
                            "
                            (valueChange)="
                              onCdeChange(cde.configuredDataElementId, 'Datetime', $event)
                            "
                          />
                        }
                        @case ('Yes/No') {
                          <div class="flex gap-2">
                            <button
                              type="button"
                              (click)="onCdeChange(cde.configuredDataElementId, 'Yes/No', true)"
                              [ngClass]="
                                cdeValues().get(cde.configuredDataElementId)?.cdvYesNo === true
                                  ? 'border-accent-400 text-accent-500 bg-accent-50'
                                  : 'border-surface-300 text-navy-700 bg-white hover:border-accent-400 hover:text-accent-500'
                              "
                              class="px-5 py-2 text-xs font-semibold border rounded-full transition-colors cursor-pointer"
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              (click)="onCdeChange(cde.configuredDataElementId, 'Yes/No', false)"
                              [ngClass]="
                                cdeValues().get(cde.configuredDataElementId)?.cdvYesNo === false &&
                                cdeValues().has(cde.configuredDataElementId)
                                  ? 'border-accent-400 text-accent-500 bg-accent-50'
                                  : 'border-surface-300 text-navy-700 bg-white hover:border-accent-400 hover:text-accent-500'
                              "
                              class="px-5 py-2 text-xs font-semibold border rounded-full transition-colors cursor-pointer"
                            >
                              No
                            </button>
                          </div>
                        }
                        @case ('Integer') {
                          <input
                            type="number"
                            step="1"
                            placeholder="Answer"
                            class="w-full"
                            [value]="cdeValues().get(cde.configuredDataElementId)?.cdvInteger ?? ''"
                            (change)="
                              onCdeChange(
                                cde.configuredDataElementId,
                                'Integer',
                                $any($event.target).value
                              )
                            "
                          />
                        }
                        @case ('Float') {
                          <input
                            type="number"
                            step="any"
                            placeholder="Answer"
                            class="w-full"
                            [value]="cdeValues().get(cde.configuredDataElementId)?.cdvFloat ?? ''"
                            (change)="
                              onCdeChange(
                                cde.configuredDataElementId,
                                'Float',
                                $any($event.target).value
                              )
                            "
                          />
                        }
                        @case ('Blob') {
                          <!-- There is no API field to store a file on a CDE value yet. -->
                          <p
                            class="px-4 py-3 text-xs text-slate-500 border border-dashed border-surface-300 rounded-xl bg-surface-50"
                          >
                            Attach this file under
                            <span class="font-semibold">Evidence &amp; Files</span>.
                          </p>
                        }
                        @default {
                          <input
                            type="text"
                            placeholder="Answer"
                            class="w-full"
                            [value]="
                              cdeValues().get(cde.configuredDataElementId)?.cdvShortVarchar ?? ''
                            "
                            (change)="
                              onCdeChange(
                                cde.configuredDataElementId,
                                cde.configuredDataElement.cdeDataType?.dataType,
                                $any($event.target).value
                              )
                            "
                          />
                        }
                      }
                    </div>
                  }
                }
              </fieldset>
            </div>
          }

          <!-- Final Determination -->
          <div
            class="bg-blue-10 rounded-2xl p-4 sm:p-6 flex sm:mx-6 flex-col gap-5 border border-accent-500/20"
            [ngClass]="canEdit() ? '' : 'mb-4 sm:mb-6'"
          >
            <div>
              <p class="text-base font-bold text-navy-900">Final Determination</p>
              <p class="text-xs text-accent-500 mt-0.5">
                Conclusion and findings based on the investigation.
              </p>
            </div>

            <fieldset [disabled]="!canEdit()" class="flex flex-col gap-5 min-w-0 border-0 p-0 m-0">
              <div
                class="flex items-center bg-slate-100 rounded-3xl px-1 py-1 gap-1 w-full sm:w-fit"
              >
                <button
                  type="button"
                  (click)="localFinding.set(1)"
                  [ngClass]="
                    localFinding() === 1
                      ? 'bg-white text-navy-900 shadow-sm'
                      : 'text-slate-500 hover:text-navy-700'
                  "
                  class="flex-1 sm:flex-none px-3 sm:px-5 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer disabled:cursor-default"
                >
                  Substantiated / Indicated
                </button>
                <button
                  type="button"
                  (click)="localFinding.set(0)"
                  [ngClass]="
                    localFinding() === 0
                      ? 'bg-white text-navy-900 shadow-sm'
                      : 'text-slate-500 hover:text-navy-700'
                  "
                  class="flex-1 sm:flex-none px-3 sm:px-5 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer disabled:cursor-default"
                >
                  Unsubstantiated / Unfounded
                </button>
              </div>
              @if (canEdit() && localFinding() !== null) {
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1 -mt-2">
                  <p class="text-2xs text-slate-500">
                    Saving a determination closes the investigation.
                  </p>
                  <button
                    type="button"
                    (click)="localFinding.set(null); closeAttempted.set(false)"
                    class="text-2xs font-semibold text-accent-500 hover:text-accent-600 underline cursor-pointer"
                  >
                    Clear determination
                  </button>
                </div>
              }

              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold text-navy-700"
                  >Date of Determination/Finding</label
                >
                <app-date-picker
                  [value]="localFindingDate() ?? ''"
                  [disabled]="!canEdit()"
                  (valueChange)="localFindingDate.set($event || null)"
                />
              </div>
            </fieldset>

            @if (isClosed()) {
              <div
                class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-4 border-t border-accent-500/20"
              >
                <p class="text-xs text-slate-500">
                  This investigation is closed and can no longer be edited.
                </p>
                @if (canReopen()) {
                  <button
                    type="button"
                    (click)="showReopenConfirm.set(true)"
                    [disabled]="reopening()"
                    class="shrink-0 px-4 py-2 text-xs font-semibold text-accent-500 bg-white border border-accent-500 rounded-xl hover:bg-accent-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ reopening() ? 'Reopening…' : 'Reopen investigation' }}
                  </button>
                }
              </div>
            }
          </div>

          <!-- Save / Finalize button -->
          @if (canEdit()) {
            <div class="flex flex-col items-end gap-2 py-4 sm:px-6">
              @if (closeAttempted() && willClose() && missingForClose().length) {
                <p class="text-xs text-red-500 text-right">
                  To close the investigation, complete: {{ missingForClose().join(', ') }}.
                </p>
              }
              <button
                type="button"
                (click)="onSave()"
                [disabled]="saving()"
                [ngClass]="
                  willClose()
                    ? 'bg-accent-500 hover:opacity-90 text-white'
                    : 'bg-navy-800 hover:bg-navy-700 text-white'
                "
                class="px-6 py-2.5 text-sm font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                @if (saving()) {
                  Saving…
                } @else if (willClose()) {
                  Finalize &amp; Close
                } @else {
                  Save
                }
              </button>
            </div>
          }
        </div>

        <!-- Evidence & Files -->
        <div
          class="w-full min-w-0 xl:w-80 xl:shrink-0 flex flex-col gap-4 sm:px-6 xl:px-0 xl:pl-6 py-6 sm:py-12"
        >
          <div>
            <p class="text-sm font-bold text-navy-600 leading-4">Evidence &amp; Files</p>
            <p class="text-xs text-slate-500 leading-4 mt-1.5">
              Supporting documentation and case evidence
            </p>
          </div>
          <app-file-uploader
            [investigationId]="inv.id"
            [existingDocuments]="mappedDocuments()"
            (uploaded)="loadInvestigation(inv.id)"
            (deleted)="loadInvestigation(inv.id)"
            [vertical]="true"
            [readonly]="!canEdit()"
          />
        </div>
      </div>

      <!-- Mobile: Case Context trigger bar -->
      <div
        class="lg:hidden flex items-center gap-3 px-4 py-3 bg-white border-t-2 border-surface-300 cursor-pointer shrink-0"
        (click)="sidebarOpen.set(true)"
      >
        <div class="w-2 h-2 rounded-full bg-accent-500 shrink-0"></div>
        <div class="flex-1 min-w-0">
          <p class="text-xs font-semibold text-navy-700">Case Context</p>
          @if (activeIncident(); as inc) {
            <p class="text-2xs text-accent-500">#{{ inc.incidentNumber }}</p>
          }
        </div>
        <img src="/icons/chevron-down-icon.svg" class="w-4 h-4 opacity-40 -rotate-90" alt="" />
      </div>
    </div>

    <!-- Mobile overlay -->
    <div
      class="lg:hidden absolute inset-0 z-20 bg-navy-900/50 transition-opacity duration-300"
      [ngClass]="sidebarOpen() ? 'opacity-100' : 'opacity-0 pointer-events-none'"
      (click)="sidebarOpen.set(false)"
    ></div>

    <!-- Mobile left panel -->
    <div
      class="lg:hidden absolute top-0 left-0 bottom-0 w-72 z-30 bg-surface-50 flex flex-col overflow-hidden shadow-2xl transition-transform duration-300"
      [ngClass]="sidebarOpen() ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Panel header -->
      <div class="bg-navy-800 px-5 py-4 flex items-center justify-between shrink-0">
        <p class="text-sm font-semibold text-white">
          {{ addingIncident() ? 'Link Incident' : 'Case Context' }}
        </p>
        <button type="button" (click)="sidebarOpen.set(false)" class="cursor-pointer">
          <img src="/icons/x-icon.svg" class="w-4 h-4 brightness-0 invert opacity-70" alt="Close" />
        </button>
      </div>

      <!-- Incident tabs strip -->
      <div class="flex items-stretch bg-white border-b border-surface-200 shrink-0">
        <!-- Scrollable tabs -->
        <div class="flex items-stretch overflow-x-auto flex-1 scrollbar-hide">
          @for (inc of inv.incidentInvestigations; track inc.id; let i = $index) {
            <button
              type="button"
              (click)="activeIncidentIndex.set(i); addingIncident.set(false)"
              class="px-3 py-2.5 text-xs font-semibold whitespace-nowrap shrink-0 cursor-pointer border-b-2 transition-all"
              [ngClass]="
                !addingIncident() && activeIncidentIndex() === i
                  ? 'text-navy-800 border-accent-500 bg-surface-50'
                  : 'text-slate-400 border-transparent hover:text-navy-600 hover:bg-surface-50'
              "
            >
              #{{ inc.incidentNumber }}
            </button>
          }
        </div>
        <!-- Fixed add button -->
        @if (canEdit()) {
          <button
            type="button"
            (click)="startAddIncident()"
            class="shrink-0 px-3 py-2.5 text-xs font-bold border-l border-surface-200 border-b-2 cursor-pointer transition-all"
            [ngClass]="
              addingIncident()
                ? 'text-accent-500 border-b-accent-500'
                : 'text-slate-400 border-b-transparent hover:text-accent-500'
            "
          >
            + Add
          </button>
        }
      </div>

      <!-- Add incident form -->
      @if (addingIncident()) {
        <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-4">
          <p class="text-2xs text-slate-500">Select an incident to link to this investigation.</p>
          <div class="flex flex-col gap-1.5">
            <label class="text-2xs font-semibold text-slate-500 uppercase tracking-wider"
              >Incident</label
            >
            <app-select
              placeholder="Select incident…"
              [options]="incidentOptions()"
              [value]="newIncidentId()"
              [loading]="loadingIncidentOptions()"
              [hasMore]="incidentsHasMore()"
              [searchable]="true"
              (changed)="newIncidentId.set($event)"
              (loadMore)="onLoadMoreIncidents()"
            />
          </div>
          <div class="flex flex-col gap-2 mt-1">
            <button
              type="button"
              (click)="onSaveNewIncident()"
              [disabled]="!newIncidentId() || savingIncident()"
              class="w-full py-2.5 text-xs font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              {{ savingIncident() ? 'Saving…' : 'Save' }}
            </button>
            <button
              type="button"
              (click)="cancelAddIncident()"
              [disabled]="savingIncident()"
              class="w-full py-2 text-xs font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      } @else {
        <!-- Panel scrollable content -->
        <div class="flex-1 overflow-y-auto p-5 flex flex-col gap-5">
          <div class="flex flex-col">
            <p class="text-2xs leading-3 font-normal text-slate-500">Case Context</p>
            @if (activeIncident(); as inc) {
              <div class="flex items-center justify-between mb-6">
                <p class="text-sm leading-4 font-semibold text-navy-600">Linked Incident</p>
                <button
                  type="button"
                  (click)="goToIncident(inc.incidentId, $event); sidebarOpen.set(false)"
                  class="text-sm font-bold text-accent-500 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  #{{ inc.incidentNumber }}
                </button>
              </div>
              @if (activeIncident()) {
                <div
                  class="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-surface-200 shadow-sm"
                >
                  <div
                    class="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center shrink-0"
                  >
                    <img src="/icons/check-white-icon.svg" class="w-4 h-4" alt="" />
                  </div>
                  <div>
                    @if (!activeStageIsTerminal()) {
                      <p class="text-2xs text-slate-400">Stage {{ activeStage() }}</p>
                    }
                    <p class="text-xs font-bold text-navy-900">{{ activeStageMeta().name }}</p>
                  </div>
                </div>
              } @else {
                <div
                  class="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-surface-200"
                >
                  <div class="w-9 h-9 rounded-full bg-surface-200 animate-pulse shrink-0"></div>
                  <div class="flex flex-col gap-2 flex-1">
                    <div class="h-2 rounded bg-surface-200 animate-pulse w-1/3"></div>
                    <div class="h-3 rounded bg-surface-200 animate-pulse w-2/3"></div>
                  </div>
                </div>
              }
            }
          </div>

          <div class="h-px bg-surface-200"></div>

          @if (inv.investigatingProgramName) {
            <div class="flex flex-col gap-1">
              <p class="text-xs leading-4 text-slate-500">Investigating Program</p>
              <p class="text-xs leading-4 font-bold text-navy-900">
                {{ inv.investigatingProgramName }}
              </p>
            </div>
          }

          @if (inv.personSendingNoticeName) {
            <div class="flex flex-col gap-2">
              <p class="text-xs leading-4 text-slate-500">Person Sending Notice</p>
              <div class="flex items-center gap-2.5">
                <div
                  class="w-6 h-6 rounded-full bg-surface-100 flex items-center justify-center shrink-0"
                >
                  <img src="/icons/user-icon.svg" class="w-3.5 h-3.5 opacity-40" alt="" />
                </div>
                <span class="text-xs font-bold text-navy-900">{{
                  inv.personSendingNoticeName
                }}</span>
              </div>
            </div>
          }

          @if (activeIncident()?.piInvolvements?.length) {
            <div class="flex flex-col gap-2">
              <p class="text-xs text-slate-500">Clients Involved</p>
              <div class="flex flex-col gap-2">
                @for (pi of activeIncident()!.piInvolvements; track pi.id) {
                  <div class="flex items-start gap-2.5">
                    <div
                      class="w-6 h-6 rounded-full bg-surface-100 flex items-center justify-center shrink-0"
                    >
                      <img src="/icons/user-icon.svg" class="w-3.5 h-3.5 opacity-40" alt="" />
                    </div>
                    <div class="flex flex-col min-w-0 pt-0.5">
                      <span class="text-xs font-bold text-navy-900 wrap-break-word"
                        >{{ pi.firstName }} {{ pi.surnames }}</span
                      >
                      @if (pi.piInvolvementType.name) {
                        <span class="text-2xs text-slate-500 wrap-break-word">{{
                          pi.piInvolvementType.name
                        }}</span>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          @if (activeIncident()?.incidentCategorizations?.length) {
            <div class="flex flex-col gap-2">
              <p class="text-xs text-slate-500">Incident Category</p>
              <div class="flex flex-wrap gap-1.5">
                @for (cat of activeIncident()!.incidentCategorizations ?? []; track cat.id) {
                  <span
                    class="px-3 py-1 text-xs font-semibold text-navy-600 border border-surface-300 rounded-full bg-white"
                  >
                    {{ cat.situationCategoryName }}
                  </span>
                }
              </div>
            </div>
          }

          <div class="h-px bg-surface-200"></div>

          <div class="flex flex-col gap-1">
            <p class="text-xs font-semibold text-slate-500 mb-2">Timeline</p>
            <div class="flex flex-col">
              @if (activeIncident()?.incidentDate) {
                <div class="flex gap-3">
                  <div class="flex flex-col items-center">
                    <div
                      class="w-6 h-6 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0"
                    >
                      <img src="/icons/clock-icon.svg" class="w-3 h-3 opacity-50" alt="" />
                    </div>
                    @if (inv.investigationNoticeDate || inv.findingDate) {
                      <div class="w-px flex-1 bg-surface-300 my-1.5"></div>
                    }
                  </div>
                  <div class="pb-4">
                    <p class="text-xs text-slate-400">Incident Occurred</p>
                    <p class="text-xs font-bold text-navy-900">
                      {{ formatDate(activeIncident()!.incidentDate) }}
                    </p>
                  </div>
                </div>
              }
              @if (inv.investigationNoticeDate) {
                <div class="flex gap-3">
                  <div class="flex flex-col items-center">
                    <div
                      class="w-6 h-6 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0"
                    >
                      <img src="/icons/clock-icon.svg" class="w-3 h-3 opacity-50" alt="" />
                    </div>
                    @if (inv.findingDate) {
                      <div class="w-px flex-1 bg-surface-300 my-1.5"></div>
                    }
                  </div>
                  <div class="pb-4">
                    <p class="text-xs text-slate-400">Notice Generated</p>
                    <p class="text-xs font-bold text-navy-900">
                      {{ formatDate(inv.investigationNoticeDate) }}
                    </p>
                  </div>
                </div>
              }
              @if (inv.findingDate) {
                <div class="flex gap-3">
                  <div class="flex flex-col items-center">
                    <div
                      class="w-6 h-6 rounded-full bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0"
                    >
                      <img src="/icons/clock-icon.svg" class="w-3 h-3 opacity-50" alt="" />
                    </div>
                  </div>
                  <div>
                    <p class="text-xs text-slate-400">Finding Date</p>
                    <p class="text-xs font-bold text-navy-900">{{ formatDate(inv.findingDate) }}</p>
                  </div>
                </div>
              }
            </div>
          </div>

          @if (canEdit() && activeIncident()) {
            <div class="h-px bg-surface-200"></div>
            <div class="flex flex-col gap-1.5">
              <button
                type="button"
                (click)="askRemoveIncident(activeIncident()!)"
                [disabled]="!canRemoveIncident() || removingIncident()"
                class="flex items-center justify-center gap-2 w-full py-2.5 text-xs font-semibold text-red-500 bg-white border border-red-200 rounded-xl hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                <img src="/icons/trash-red-icon.svg" class="w-3.5 h-3.5" alt="" />
                {{ removingIncident() ? 'Removing…' : 'Remove incident' }}
              </button>
              @if (!canRemoveIncident()) {
                <p class="text-2xs text-slate-400 text-center">
                  An investigation needs at least one linked incident.
                </p>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>

  @if (showCloseConfirm()) {
    <app-confirm-modal
      title="Close Investigation?"
      confirmLabel="Close"
      (closed)="showCloseConfirm.set(false)"
      (confirmed)="onConfirmClose()"
    >
      You are about to close
      <strong class="font-bold text-navy-900">{{
        inv.investigationNumber ?? 'this investigation'
      }}</strong>
      as <strong class="font-bold text-navy-900 whitespace-nowrap">{{ findingLabel() }}</strong
      >.
    </app-confirm-modal>
  }

  @if (showReopenConfirm()) {
    <app-confirm-modal
      title="Reopen investigation"
      [message]="
        'Reopen ' +
        (inv.investigationNumber ?? 'this investigation') +
        '? The current determination (' +
        findingLabelFor(inv.finding) +
        ') and its date will be cleared, and the investigation will be editable again until it is closed with a new determination.'
      "
      confirmLabel="Reopen"
      (closed)="showReopenConfirm.set(false)"
      (confirmed)="onConfirmReopen()"
    />
  }

  @if (incidentToRemove(); as target) {
    <app-confirm-modal
      title="Remove incident"
      confirmLabel="Remove"
      (closed)="incidentToRemove.set(null)"
      (confirmed)="onConfirmRemoveIncident()"
    >
      Remove incident <strong class="font-bold text-navy-900">#{{ target.incidentNumber }}</strong>
      from the current investigation? The incident record will not be deleted.
    </app-confirm-modal>
  }
} @else {
  <div class="flex items-center justify-center h-full">
    <p class="text-sm text-slate-400">Investigation not found.</p>
  </div>
}
